import {DATA_FRAME_DISPLAY_ROW_LIMIT} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisGraphQL, AnalysisViewGraphQL} from 'analysis-tool/AnalysisTypes';
import {isViewTerminalStepType} from 'analysis-tool/data/LineageStepTypes';
import {
  ButtonDataViewAction,
  DataViewActionInput,
  rowSelectionState,
} from 'analysis-tool/data-view/DataViewContext';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {recordAnalysisAction} from 'analysis-tool/ui/debugAnalysisActionHistory';
import {openFlagRowsModal} from 'analysis-tool/ui/flag-rows/FlagRowsModal';
import {getFlaggedRowsFromDataFrame} from 'analysis-tool/ui/flag-rows/FlagRowUtil';
import {assert} from 'Asserts';
import {PersistedDataFrameGraphQL} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import hasDownstreamFlagRowsStep from 'pipelines/hasDownstreamFlagRowsStep';
import {StepReference} from 'pipelines/network/PipelineDAO';
import {getChildStepsForDataFrame} from 'pipelines/StepLineageUtils';
import {getLegacyStepFromAnalysis} from 'pipelines/StepUtil';

export default function getFlaggingActions(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL,
  clientId: string,
  selectedRowIndices: Set<number>
): Array<ButtonDataViewAction | null> {
  if (!isFlaggingEnabled(analysis, view, selectedRowIndices)) {
    return [];
  }

  return [
    getFlagRowsAction(analysis, view, clientId, selectedRowIndices),
    getClearFlagRowsAction(analysis, view, clientId, selectedRowIndices),
  ];
}

function isFlaggingEnabled(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL,
  selectedRowIndices: Set<number>
): boolean {
  return (
    !isAnalysisReadOnly(analysis) &&
    view.lineage.every(({step}) => step.statusCode === PipelineStepStatusCode.SUCCEEDED) &&
    selectedRowIndices.size > 0
  );
}

function getFlagRowsAction(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL,
  clientId: string,
  selectedRowIndices: Set<number>
): ButtonDataViewAction {
  return ({persistedDataFrame}: DataViewActionInput) => {
    if (persistedDataFrame == null) {
      return null;
    }

    const flaggedRowIndices = getFlaggedRowIndices(analysis, view, persistedDataFrame);
    const disabledMessage = getFlagRowDisabledMessage(analysis, view, persistedDataFrame);

    return {
      icon: 'flag',
      isAlternativeButtonColor: true,
      onClick: () => {
        onToggleFlaggedRows(
          analysis,
          view,
          persistedDataFrame,
          clientId,
          selectedRowIndices,
          flaggedRowIndices,
          true
        );
      },
      helpText: disabledMessage ?? 'Flag rows',
      label: 'Flag',
      isDisabled: disabledMessage != null,
    };
  };
}

function getFlagRowDisabledMessage(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL,
  persistedDataFrame: PersistedDataFrameGraphQL
): string | null {
  const lastStep = view.lineage[view.lineage.length - 1];
  const legacyLastStep = getLegacyStepFromAnalysis(analysis, lastStep.step);

  if (persistedDataFrame.numRows > DATA_FRAME_DISPLAY_ROW_LIMIT) {
    return 'Flagging rows is disabled for large tables.';
  }

  if (hasDownstreamFlagRowsStep(analysis, legacyLastStep)) {
    return 'This view cannot be flagged because one or more downstream views contain flagged rows.';
  }

  return null;
}

function getClearFlagRowsAction(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL,
  clientId: string,
  selectedRowIndices: Set<number>
): ButtonDataViewAction {
  return ({persistedDataFrame}: DataViewActionInput) => {
    if (persistedDataFrame == null) {
      return null;
    }

    const flaggedRowIndices = getFlaggedRowIndices(analysis, view, persistedDataFrame);
    const disabledMessage = getClearFlagRowDisabledMessage(
      analysis,
      view,
      persistedDataFrame,
      selectedRowIndices,
      flaggedRowIndices
    );

    return {
      icon: 'flag-slash',
      isAlternativeButtonColor: true,
      onClick: () => {
        onToggleFlaggedRows(
          analysis,
          view,
          persistedDataFrame,
          clientId,
          selectedRowIndices,
          flaggedRowIndices,
          false
        );
      },
      helpText: disabledMessage ?? 'Clear flag rows',
      label: 'Clear',
      isDisabled: disabledMessage != null,
    };
  };
}

function getClearFlagRowDisabledMessage(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL,
  persistedDataFrame: PersistedDataFrameGraphQL,
  selectedRowIndices: Set<number>,
  flaggedRowIndices: Set<string>
): string | null {
  const lastStep = view.lineage[view.lineage.length - 1];
  const legacyLastStep = getLegacyStepFromAnalysis(analysis, lastStep.step);

  if (persistedDataFrame.numRows > DATA_FRAME_DISPLAY_ROW_LIMIT) {
    return 'Clearing flags is disabled for large tables.';
  }

  if ([...selectedRowIndices].every((rowIndex) => !flaggedRowIndices.has(String(rowIndex)))) {
    return 'No flagged rows are selected.';
  }

  if (hasDownstreamFlagRowsStep(analysis, legacyLastStep)) {
    return 'This view cannot be flagged because one or more downstream views contain flagged rows.';
  }

  return null;
}

export function getFlaggedRowIndices(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL,
  persistedDataFrame: PersistedDataFrameGraphQL
): Set<string> {
  const lastNode = view.lineage[view.lineage.length - 1];

  let nodeToCheck = lastNode;
  if (isViewTerminalStepType(lastNode.step.stepType)) {
    if (view.lineage.length >= 2) {
      nodeToCheck = view.lineage[view.lineage.length - 2];
    }
  }

  const legacyLastStep = getLegacyStepFromAnalysis(analysis, nodeToCheck.step);

  return getFlaggedRowsFromDataFrame(analysis, legacyLastStep, persistedDataFrame.dataFrameJson.rows);
}

function onToggleFlaggedRows(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL,
  persistedDataFrame: PersistedDataFrameGraphQL,
  clientId: string,
  selectedRowIndices: Set<number>,
  flaggedRowIndices: Set<string>,
  isAddingFlag: boolean
): void {
  const lastNode = view.lineage[view.lineage.length - 1];
  const parentNode = isViewTerminalStepType(lastNode.step.stepType)
    ? view.lineage.length >= 2
      ? view.lineage[view.lineage.length - 2]
      : null
    : lastNode;

  assert(
    parentNode != null,
    'Expected parent node to not be null. This only happens if a view that has a terminal view step type only have 1 node.',
    {
      stepTypes: view.lineage.map(({step}) => step.stepType),
    }
  );
  assert(
    parentNode.stepOutputReference != null && parentNode.stepOutputReference.rawDataIndex != null,
    'Expected parent node to have an step output reference and raw data index. Are we rendering flagging button on non succeeded views?'
  );

  recordAnalysisAction(isAddingFlag ? 'FLAG_ROWS' : 'CLEAR_FLAG_ROWS', {
    analysisId: analysis.id,
    lastNodeStepType: lastNode.step.stepType,
    parentStepType: parentNode.step.stepType,
    selectedRowCount: selectedRowIndices.size,
  });

  const legacyParentStep = getLegacyStepFromAnalysis(analysis, parentNode.step);

  const rawData =
    parentNode.stepOutputReference.outputStepData.rawDatas[parentNode.stepOutputReference.rawDataIndex];

  const childSteps = getChildStepsForDataFrame(analysis, legacyParentStep, rawData.id);

  const outputDataReferences =
    childSteps.length > 0
      ? childSteps.flatMap((childStep) =>
          childStep.inputStepDatas
            .filter((stepData) => stepData.dataFrames.some((dataFrame) => dataFrame.id === rawData.id))
            .map((inputStepData) => ({
              stepId: childStep.id,
              stepDataName: inputStepData.name,
            }))
        )
      : undefined;

  openFlagRowsModal({
    analysis,
    selectedRowIds: new Set([...selectedRowIndices].map((index) => String(index))),
    flaggedRowIds: flaggedRowIndices,
    isAddingFlag,
    parentStepReference: {
      stepReference: {stepId: parentNode.step.id, type: StepReference.STEP_ID},
      stepDataName: parentNode.stepOutputReference.outputStepData.name,
    },
    outputDataReferences,
    onFinalizeFlagging: () => {
      rowSelectionState.deleteState(clientId);
    },
  });
}
